import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { AlertTriangle, CalendarOff, Copy, Pencil, Plus, RotateCcw, Send, Sparkles, Trash2, Undo2, CheckCircle2, XCircle } from 'lucide-react'
import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Textarea } from '@jira-clone/ui'
import { CATEGORY_LABEL } from '@jira-clone/shared-types'
import { getApiErrorMessage } from '../../../shared/utils/apiError'
import { formatDuration, shortDay, STATUS_LABEL, weekdayName, weekDates } from '../../../lib/timesheet'
import {
  useSheetActions,
  useSuggestions,
  useTimesheetConfig,
  useTimesheetOptions,
  type RowFormValues,
  type TimesheetRowDto,
  type WeekPayload,
} from '../api'
import { EntryDialog } from './EntryDialog'

const statusVariant = (s: string) => (s === 'approved' ? 'success' : s === 'submitted' ? 'outline' : s === 'rejected' ? 'destructive' : 'secondary')

function ReasonDialog({
  open,
  title,
  confirm,
  onClose,
  onConfirm,
  busy,
}: {
  open: boolean
  title: string
  confirm: string
  onClose: () => void
  onConfirm: (reason: string) => void
  busy: boolean
}) {
  const [reason, setReason] = useState('')
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder="Say why" aria-label="Reason" autoFocus />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || reason.trim() === ''}
            onClick={() => {
              onConfirm(reason.trim())
              setReason('')
            }}
          >
            {confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The links and details a row carries, as short text. */
function rowDetails(r: TimesheetRowDto): string[] {
  return [
    r.projectName,
    r.taskTitle,
    r.leadName && `Lead: ${r.leadName}`,
    r.dealName && `Deal: ${r.dealName}`,
    r.campaign && `Campaign: ${r.campaign}`,
    r.channel,
    r.deliverable && `Deliverable: ${r.deliverable}`,
    r.revisionRound != null && `Revision ${r.revisionRound}`,
    r.filingPeriod && `Period ${r.filingPeriod}`,
  ].filter(Boolean) as string[]
}

/**
 * One timesheet: its summary, each day's entries, and what can be done to it (add and edit entries,
 * submit, withdraw, approve, send back, reopen), according to the permissions the server sent.
 */
export function SheetPanel({ payload, weekStart }: { payload: WeekPayload; weekStart?: string | null }) {
  const { sheet, rows, plan, days, workedMinutes, expectedMinutes, warnings, config, permissions } = payload
  const actions = useSheetActions(sheet.id, weekStart)
  const configQuery = useTimesheetConfig()
  const options = useTimesheetOptions(permissions.canEdit)

  const [entry, setEntry] = useState<{ date: string; row: TimesheetRowDto | null } | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [reasonFor, setReasonFor] = useState<'reject' | 'reopen' | null>(null)
  const [notes, setNotes] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const suggestionsQuery = useSuggestions(sheet.id, permissions.canEdit)
  const suggestions = suggestionsQuery.data?.suggestions ?? []

  const dates = weekDates(sheet.weekStartDate)
  const activityLabel = useMemo(() => new Map(config.activities.map((a) => [a.key, a.label])), [config])
  const pct = expectedMinutes > 0 ? Math.min(100, Math.round((workedMinutes / expectedMinutes) * 100)) : workedMinutes > 0 ? 100 : 0
  const busy = Object.values(actions).some((m) => m.isPending)

  const run = async (fn: () => Promise<unknown>, doing: string, success?: string) => {
    try {
      await fn()
      if (success) toast.success(success)
    } catch (err) {
      toast.error(getApiErrorMessage(err, doing))
    }
  }

  const save = async (values: RowFormValues) => {
    setServerError(null)
    try {
      if (entry?.row) await actions.updateRow.mutateAsync({ rowId: entry.row.id, values })
      else await actions.addRow.mutateAsync(values)
      setEntry(null)
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'save this entry'))
    }
  }

  const addSuggestions = async (refs: string[]) => {
    try {
      const res = await actions.acceptSuggestions.mutateAsync(refs)
      setPicked(new Set())
      const added = refs.length - res.skipped.length
      if (added > 0) toast.success(added === 1 ? 'Entry added' : `${added} entries added`)
      if (res.skipped.length > 0) toast.error(`${res.skipped.length} could not be added: ${res.skipped[0].reason}`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'add these entries'))
    }
  }

  const submit = async () => {
    try {
      const res = await actions.submit.mutateAsync(undefined)
      toast.success(res.sheet.status === 'approved' ? 'Timesheet submitted and approved' : 'Timesheet submitted')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'submit this timesheet'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(sheet.status) as any} className="text-[10px] uppercase">
            {STATUS_LABEL[sheet.status]}
          </Badge>
          {sheet.userName && !permissions.canEdit && <span className="text-sm font-medium">{sheet.userName}</span>}
          {sheet.department && <span className="text-xs text-muted-foreground">{sheet.department}</span>}
          <span className="ml-auto text-sm">
            <strong>{formatDuration(workedMinutes)}</strong> logged of <strong>{formatDuration(expectedMinutes)}</strong> expected
          </span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Hours logged">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>

        {sheet.status === 'rejected' && (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <p className="font-medium">Sent back{sheet.rejectionReason ? ':' : ''}</p>
            {sheet.rejectionReason && <p>{sheet.rejectionReason}</p>}
          </div>
        )}

        {permissions.canEdit && warnings.length > 0 && (
          <ul className="space-y-1" aria-label="Things to check">
            {warnings.map((w) => (
              <li key={w} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2">
          {permissions.canEdit && (
            <>
              <Button size="sm" onClick={() => { setServerError(null); setEntry({ date: days.find((d) => d.expected > 0 && d.worked === 0)?.date ?? dates[0], row: null }) }}>
                <Plus className="h-4 w-4 mr-1" /> Add entry
              </Button>
              {rows.every((r) => r.system) && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => actions.copyLastWeek.mutateAsync(undefined), 'copy last week', 'Last week copied. Fill in the time.')}>
                  <Copy className="h-4 w-4 mr-1" /> Copy last week
                </Button>
              )}
              <Button size="sm" variant="secondary" disabled={busy} onClick={submit}>
                <Send className="h-4 w-4 mr-1" /> Submit week
              </Button>
            </>
          )}
          {permissions.canWithdraw && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => actions.withdraw.mutateAsync(undefined), 'withdraw this timesheet', 'Timesheet withdrawn')}>
              <Undo2 className="h-4 w-4 mr-1" /> Withdraw
            </Button>
          )}
          {permissions.canReview && (
            <>
              <Button size="sm" disabled={busy} onClick={() => run(() => actions.approve.mutateAsync(undefined), 'approve this timesheet', 'Timesheet approved')}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setReasonFor('reject')}>
                <XCircle className="h-4 w-4 mr-1" /> Send back
              </Button>
            </>
          )}
          {permissions.canReopen && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setReasonFor('reopen')}>
              <RotateCcw className="h-4 w-4 mr-1" /> Reopen
            </Button>
          )}
        </div>
      </div>

      {permissions.canEdit && suggestions.length > 0 && (
        <section className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" aria-label="Add from your activity">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Add from your activity</h3>
            <span className="text-xs text-muted-foreground">Time already recorded in Zynx this week. Nothing is added until you choose it.</span>
          </div>
          <ul className="divide-y rounded-lg border bg-card">
            {suggestions.map((s) => (
              <li key={s.ref} className="flex items-center gap-3 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={picked.has(s.ref)}
                  onChange={(e) =>
                    setPicked((prev) => {
                      const next = new Set(prev)
                      if (e.target.checked) next.add(s.ref)
                      else next.delete(s.ref)
                      return next
                    })
                  }
                  aria-label={`Select ${s.label} on ${shortDay(s.date)}`}
                />
                <span className="w-20 shrink-0 text-xs text-muted-foreground">
                  {weekdayName(s.date)} {shortDay(s.date)}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{activityLabel.get(s.activityType) ?? s.activityType}</span>
                  <span className="text-xs text-muted-foreground">
                    {' · '}
                    {[s.leadName, s.projectName, s.taskTitle, s.note].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <Badge variant="secondary" className="text-[10px]">{s.label}</Badge>
                <span className="font-mono tabular-nums">{formatDuration(s.durationMinutes)}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy || picked.size === 0} onClick={() => addSuggestions([...picked])}>
              Add selected ({picked.size})
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => addSuggestions(suggestions.map((s) => s.ref))}>
              Add all ({suggestions.length})
            </Button>
          </div>
        </section>
      )}

      <div className="space-y-3">
        {dates.map((date) => {
          const day = days.find((d) => d.date === date)
          const p = plan.find((x) => x.date === date)
          const dayRows = rows.filter((r) => r.date === date)
          const offDay = (p?.scheduled ?? 0) === 0
          return (
            <section key={date} className="rounded-xl border bg-card" aria-label={`${weekdayName(date)} ${shortDay(date)}`}>
              <header className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b">
                <h3 className="text-sm font-semibold">
                  {weekdayName(date)} <span className="text-muted-foreground font-normal">{shortDay(date)}</span>
                </h3>
                {p?.holiday && <Badge variant="secondary" className="text-[10px]">Holiday: {p.holiday}</Badge>}
                {p?.leave && <Badge variant="secondary" className="text-[10px]"><CalendarOff className="h-3 w-3 mr-1" />{p.leave.half ? 'Half-day leave' : 'On leave'}</Badge>}
                {offDay && !p?.holiday && <span className="text-[11px] text-muted-foreground">Day off</span>}
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDuration(day?.worked ?? 0)}
                  {(day?.expected ?? 0) > 0 && ` of ${formatDuration(day?.expected)}`}
                </span>
                {permissions.canEdit && (
                  <Button size="sm" variant="ghost" className="h-7 px-2" aria-label={`Add entry on ${weekdayName(date)}`} onClick={() => { setServerError(null); setEntry({ date, row: null }) }}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </header>
              {dayRows.length > 0 && (
                <ul className="divide-y">
                  {dayRows.map((r) => {
                    const details = rowDetails(r)
                    const empty = r.durationMinutes === 0
                    return (
                      <li key={r.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2">
                            <span className="font-medium">
                              {r.system ? CATEGORY_LABEL[r.category] : r.category === 'work' ? activityLabel.get(r.activityType ?? '') ?? r.activityType : CATEGORY_LABEL[r.category]}
                            </span>
                            {details.length > 0 && <span className="text-xs text-muted-foreground">{details.join(' · ')}</span>}
                          </div>
                          {r.note && <p className="text-xs text-muted-foreground mt-0.5">{r.note}</p>}
                        </div>
                        <span className={`font-mono text-sm tabular-nums ${empty ? 'text-amber-600' : ''}`}>{empty ? 'Add time' : formatDuration(r.durationMinutes)}</span>
                        {permissions.canEdit && !r.system && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Edit entry" onClick={() => { setServerError(null); setEntry({ date: r.date, row: r }) }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Delete entry" disabled={busy} onClick={() => run(() => actions.deleteRow.mutateAsync(r.id), 'delete this entry')}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          )
        })}
      </div>

      {(permissions.canEdit || sheet.notes) && (
        <div className="rounded-xl border bg-card p-4 space-y-2">
          <h3 className="text-sm font-semibold">Notes for the week</h3>
          {permissions.canEdit ? (
            <Textarea
              value={notes ?? sheet.notes ?? ''}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== null && notes !== (sheet.notes ?? '') && run(() => actions.saveNotes.mutateAsync(notes), 'save the notes')}
              rows={2}
              maxLength={1000}
              placeholder="Anything your approver should know"
              aria-label="Notes for the week"
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap">{sheet.notes}</p>
          )}
        </div>
      )}

      {sheet.history.length > 1 && (
        <div className="rounded-xl border bg-card p-4">
          <button type="button" className="text-sm font-semibold" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
            History ({sheet.history.length})
          </button>
          {showHistory && (
            <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground">
              {sheet.history.map((h, i) => (
                <li key={`${h.at}-${i}`}>
                  <span className="font-medium text-foreground">{h.action[0].toUpperCase() + h.action.slice(1)}</span>
                  {h.byName ? ` by ${h.byName}` : ''} · {new Date(h.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {h.reason ? ` · ${h.reason}` : ''}
                  {h.totalBefore != null && h.totalAfter != null ? ` · ${formatDuration(h.totalBefore)} → ${formatDuration(h.totalAfter)}` : ''}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {entry && configQuery.data && (
        <EntryDialog
          open
          onClose={() => setEntry(null)}
          date={entry.date}
          weekDates={dates}
          config={config}
          channels={configQuery.data.channels}
          options={options.data}
          initial={entry.row}
          saving={actions.addRow.isPending || actions.updateRow.isPending}
          serverError={serverError}
          onSave={save}
        />
      )}

      <ReasonDialog
        open={reasonFor !== null}
        title={reasonFor === 'reject' ? 'Send this timesheet back' : 'Reopen this timesheet'}
        confirm={reasonFor === 'reject' ? 'Send back' : 'Reopen'}
        busy={busy}
        onClose={() => setReasonFor(null)}
        onConfirm={(reason) => {
          const which = reasonFor
          setReasonFor(null)
          if (which === 'reject') void run(() => actions.reject.mutateAsync(reason), 'send this timesheet back', 'Timesheet sent back')
          if (which === 'reopen') void run(() => actions.reopen.mutateAsync(reason), 'reopen this timesheet', 'Timesheet reopened')
        }}
      />
    </div>
  )
}
